import { supabase } from '../supabase.js';

/* ---------------------------------------------------------------
   Alerts view — ops/admin dashboard for open dropout flags
   and yesterday's register submission gaps.
--------------------------------------------------------------- */

export async function renderAlerts() {
  const el = document.getElementById('view-alerts');
  el.innerHTML = `
    <div class="page-header">
      <h1>Alerts</h1>
      <p>Open dropout flags and register submission gaps requiring action today.</p>
    </div>
    <div id="alerts-body"><div class="att-empty">Loading…</div></div>
  `;
  await refreshAlerts();
}

async function refreshAlerts() {
  const body = document.getElementById('alerts-body');
  if (!body) return;

  const [flagsRes, reportRes] = await Promise.all([
    supabase
      .from('dropout_flags')
      .select(`
        flag_id, student_uuid, streak_start_date, flagged_at, status, status_updated_at,
        students(first_name, surname),
        classes(class_id, class_name, facilitator_id,
          facilitators(full_name, email),
          training_venues(towns(municipalities(municipality_name)))
        )
      `)
      .eq('status', 'open')
      .order('flagged_at', { ascending: true }),
    supabase.rpc('get_register_gaps'),
  ]);

  const flags = flagsRes.data ?? [];
  const gaps  = reportRes.data ?? [];

  const staleFlags   = flags.filter(f => !f.status_updated_at && hoursOpen(f.flagged_at) >= 24);
  const activeFlags  = flags.filter(f => !staleFlags.includes(f));

  body.innerHTML = `
    ${staleFlags.length ? `
    <section class="alerts-section">
      <h2 class="alerts-section-title alerts-section-title-red">
        🔴 Stale flags — no facilitator update in 24+ hours (${staleFlags.length})
      </h2>
      <div class="alerts-table-wrap">
        ${flagTable(staleFlags, true)}
      </div>
    </section>` : ''}

    <section class="alerts-section">
      <h2 class="alerts-section-title">
        ⚠️ Open dropout flags${activeFlags.length ? ` (${activeFlags.length})` : ' — none'}
      </h2>
      ${activeFlags.length
        ? `<div class="alerts-table-wrap">${flagTable(activeFlags, false)}</div>`
        : `<div class="att-empty" style="margin:0;padding:20px">No open flags right now.</div>`}
    </section>

    <section class="alerts-section">
      <h2 class="alerts-section-title">
        📋 Yesterday's register gaps${gaps.length ? ` (${gaps.length})` : ' — none'}
      </h2>
      ${gaps.length
        ? `<div class="alerts-table-wrap">${gapTable(gaps)}</div>`
        : `<div class="att-empty" style="margin:0;padding:20px">All registers submitted for yesterday. ✓</div>`}
    </section>
  `;

  // Wire status selects
  body.querySelectorAll('.flag-status-select').forEach(sel => {
    sel.addEventListener('change', async () => {
      const newStatus = sel.value;
      if (!newStatus) return;
      sel.disabled = true;
      await updateFlagStatus(sel.dataset.flagId, newStatus);
      await refreshAlerts();
    });
  });
}

function flagTable(flags, stale) {
  return `
    <table class="alerts-table">
      <thead>
        <tr>
          <th>Learner</th>
          <th>Class</th>
          <th>Municipality</th>
          <th>Facilitator</th>
          <th>Absent since</th>
          <th>Flagged</th>
          <th>Action</th>
        </tr>
      </thead>
      <tbody>
        ${flags.map(f => {
          const name   = escape(`${f.students?.first_name || ''} ${f.students?.surname || ''}`.trim());
          const cls    = escape(f.classes?.class_name || '—');
          const muni   = escape(f.classes?.training_venues?.towns?.municipalities?.municipality_name || '—');
          const fac    = escape(f.classes?.facilitators?.full_name || '—');
          const hrs    = hoursOpen(f.flagged_at);
          const ageLabel = hrs < 24 ? 'Today' : `${Math.floor(hrs / 24)}d ago`;
          return `
          <tr class="${stale ? 'alert-row-stale' : ''}">
            <td><strong>${name}</strong></td>
            <td>${cls}</td>
            <td>${muni}</td>
            <td>${fac}</td>
            <td>${f.streak_start_date}</td>
            <td>${ageLabel}</td>
            <td>
              <select class="select-input flag-status-select"
                      data-flag-id="${f.flag_id}"
                      style="font-size:.78rem;padding:3px 8px;min-width:200px">
                <option value="">— update status —</option>
                <option value="returning">Contacted — returning</option>
                <option value="confirmed_dropout">Confirmed dropout</option>
                <option value="sick_note_incoming">Sick note incoming</option>
                <option value="unreachable">Unreachable — escalate</option>
              </select>
            </td>
          </tr>`;
        }).join('')}
      </tbody>
    </table>`;
}

function gapTable(gaps) {
  return `
    <table class="alerts-table">
      <thead>
        <tr>
          <th>Class</th>
          <th>Municipality</th>
          <th>Facilitator</th>
          <th>Email</th>
        </tr>
      </thead>
      <tbody>
        ${gaps.map(g => `
          <tr>
            <td>${escape(g.class_name)}</td>
            <td>${escape(g.municipality_name)}</td>
            <td>${escape(g.facilitator_name)}</td>
            <td><a href="mailto:${escape(g.facilitator_email)}">${escape(g.facilitator_email)}</a></td>
          </tr>`).join('')}
      </tbody>
    </table>`;
}

async function updateFlagStatus(flagId, newStatus) {
  const { data: { user } } = await supabase.auth.getUser();
  const { error } = await supabase
    .from('dropout_flags')
    .update({
      status:            newStatus,
      status_updated_at: new Date().toISOString(),
      status_updated_by: user?.id ?? null,
    })
    .eq('flag_id', flagId);
  if (error) alert(`Failed to update flag: ${error.message}`);
}

function hoursOpen(iso) {
  return (Date.now() - new Date(iso).getTime()) / 3600000;
}

function escape(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}
